class GpQuickOrderMatrix extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.submitButton = null;
    this.clearButton = null;
    this.totalElement = null;
    this.itemsElement = null;
    this.messageElement = null;
    this.sectionId = '';
    this.sectionUrl = '';
    this.submitButtonText = '';
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[data-submit]');
    this.clearButton = this.querySelector('[data-clear]');
    this.totalElement = this.querySelector('[data-total]');
    this.itemsElement = this.querySelector('[data-items]');
    this.messageElement = this.querySelector('[data-message]');
    this.sectionId = this.dataset.sectionId || '';
    this.sectionUrl = this.dataset.sectionUrl || window.location.pathname;
    this.submitButtonText = this.submitButton?.textContent || '';

    if (this.dataset.statusMessage) {
      this.setMessage(this.dataset.statusMessage);
      delete this.dataset.statusMessage;
    }

    this.form?.addEventListener('submit', this.onSubmit);
    this.clearButton?.addEventListener('click', this.onClear);
    this.addEventListener('click', this.onQuantityButtonClick);
    this.addEventListener('input', this.onInput);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
    this.clearButton?.removeEventListener('click', this.onClear);
    this.removeEventListener('click', this.onQuantityButtonClick);
    this.removeEventListener('input', this.onInput);
  }

  onSubmit = (event) => {
    event.preventDefault();
    this.updateCart();
  };

  onClear = () => {
    this.inputs.forEach((input) => {
      input.value = 0;
    });
    this.updateTotals();
  };

  onInput = (event) => {
    if (!event.target.matches('[data-quantity-variant-id]')) return;
    this.normalizeInput(event.target);
    this.updateTotals();
  };

  onQuantityButtonClick = (event) => {
    const button = event.target.closest('.gp-quick-order-matrix__qty-button');
    if (!button) return;

    const quantity = button.closest('[data-quantity-wrapper]')?.querySelector('[data-quantity-variant-id]');
    if (!quantity) return;

    const step = Number(quantity.step) || 1;
    const current = Number(quantity.value) || 0;
    quantity.value = button.name === 'minus' ? current - step : current + step;
    this.normalizeInput(quantity);
    this.updateTotals();
  };

  get inputs() {
    return Array.from(this.querySelectorAll('[data-quantity-variant-id]'));
  }

  normalizeInput(input) {
    const min = Number(input.min) || 0;
    const max = Number(input.max) || 9999;
    const step = Number(input.step) || 1;
    let value = Number(input.value);

    if (!Number.isFinite(value) || value <= 0) {
      input.value = 0;
      return;
    }

    if (input.dataset.removeOnly === 'true') {
      const cartQuantity = Number(input.dataset.cartQuantity) || 0;
      input.value = Math.floor(Math.min(value, cartQuantity, max));
      return;
    }

    if (value > 0 && value < min) value = min;
    if (step > 1) value = min + Math.ceil((value - min) / step) * step;
    if (value > max) {
      const maxOffset = Math.floor((max - min) / step) * step;
      value = maxOffset >= 0 ? min + maxOffset : max;
    }

    input.value = value;
  }

  getUpdates() {
    return this.inputs.reduce((updates, input) => {
      this.normalizeInput(input);

      const variantId = input.dataset.quantityVariantId;
      const quantity = Number(input.value) || 0;
      const cartQuantity = Number(input.dataset.cartQuantity) || 0;

      if (!variantId) return updates;
      if (cartQuantity === 0 && quantity === 0) return updates;
      if (cartQuantity > 0 && quantity === cartQuantity) return updates;

      updates[variantId] = quantity;
      return updates;
    }, {});
  }

  updateTotals() {
    const totals = this.inputs.reduce((summary, input) => {
      const quantity = Number(input.value) || 0;
      const price = Number(input.dataset.price) || 0;
      summary.items += quantity;
      summary.cents += quantity * price;
      return summary;
    }, { items: 0, cents: 0 });

    if (this.totalElement) {
      this.totalElement.dataset.totalCents = String(totals.cents);
      this.totalElement.textContent = this.formatMoney(totals.cents);
    }

    if (this.itemsElement) {
      this.itemsElement.textContent = `${totals.items} ${totals.items === 1 ? 'articolo' : 'articoli'}`;
    }

    if (this.clearButton) {
      this.clearButton.hidden = totals.items === 0;
    }
  }

  async updateCart() {
    this.setMessage('');

    const updates = this.getUpdates();

    if (Object.keys(updates).length === 0) {
      this.setMessage('Seleziona almeno una quantita o modifica il carrello.');
      return;
    }

    this.setLoading(true);
    this.dispatchThemeEvent('theme:loading:doing');

    try {
      const body = {
        updates
      };

      const response = await fetch(this.cartUpdateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(body)
      });

      const cart = await response.json().catch(() => null);
      if (!cart) throw new Error('Risposta carrello non valida.');
      if (!response.ok) throw new Error(this.getCartErrorMessage(cart));
      if (cart.errors) throw new Error(this.getCartErrorMessage(cart));

      if (!this.cartUrl) throw new Error('URL carrello non disponibile.');

      this.dispatchThemeEvent(this.themeEvent('cartReload', 'cart:reload'));
      this.dispatchThemeEvent(this.themeEvent('currencyUpdate', 'currency:update'));
      window.location.assign(this.cartUrl);
    } catch (error) {
      this.setMessage(error.message || 'Errore durante aggiornamento carrello.', true);
    } finally {
      this.dispatchThemeEvent('theme:loading:done');
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    this.toggleAttribute('aria-busy', isLoading);
    this.submitButton?.toggleAttribute('disabled', isLoading);
    this.clearButton?.toggleAttribute('disabled', isLoading);

    if (this.submitButton) {
      this.submitButton.textContent = isLoading ? this.submitButton.dataset.loadingText || 'AGGIORNAMENTO...' : this.submitButtonText;
    }
  }

  setMessage(message, isError = false) {
    if (!this.messageElement) return;
    this.messageElement.textContent = message;
    this.messageElement.setAttribute('role', isError ? 'alert' : 'status');

    if (isError && message) {
      this.messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  getCartErrorMessage(cart) {
    const fallback = 'Aggiornamento carrello non riuscito.';
    if (!cart) return fallback;
    if (typeof cart.description === 'string' && cart.description) return cart.description;
    if (typeof cart.message === 'string' && cart.message) return cart.message;
    if (typeof cart.errors === 'string' && cart.errors) return cart.errors;
    if (Array.isArray(cart.errors) && cart.errors.length > 0) return cart.errors.join(' ');
    if (cart.errors && typeof cart.errors === 'object') return Object.values(cart.errors).flat().join(' ') || fallback;
    return fallback;
  }

  themeEvent(key, fallback) {
    return window.ThemeEvents?.[key] || fallback;
  }

  dispatchThemeEvent(name) {
    if (!name) return;
    document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
  }

  get cartUpdateUrl() {
    return this.dataset.cartUpdateUrl || this.form?.action || `${window.Shopify?.routes?.root || '/'}cart/update.js`;
  }

  get cartUrl() {
    return this.dataset.cartUrl || '';
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, this.dataset.moneyFormat);
    }

    const currency = this.dataset.currency || window.Shopify?.currency?.active || 'EUR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'it-IT', {
        style: 'currency',
        currency
      }).format(cents / 100);
    } catch (error) {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  }
}

if (!customElements.get('gp-quick-order-matrix')) {
  customElements.define('gp-quick-order-matrix', GpQuickOrderMatrix);
}
